/**
 * persist.ts — 세이브 저장 레이어
 *
 * 네이티브(iOS/Android): @capacitor/preferences (UserDefaults / SharedPreferences)
 *   - WebView localStorage는 OS가 저장공간 정리·캐시 삭제 시 지울 수 있어 세이브 유실 위험
 *   - 부팅 시 initStorage()가 전체 키를 메모리 캐시에 적재 → get은 동기
 *   - set은 메모리 캐시 즉시 갱신 + Preferences 비동기 write-through (+ localStorage 미러)
 * 웹/토스: localStorage 그대로 사용 (Preferences 웹 구현도 localStorage라 이득 없음)
 *
 * 기존 유저 마이그레이션: Preferences에 게임 키가 하나도 없으면
 * localStorage의 swordEnhance_* 키를 Preferences로 1회 복사한다.
 * 이후에는 Preferences가 소스 오브 트루스.
 */
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

const KEY_PREFIX = 'swordEnhance_';

const memCache = new Map<string, string>();
let useNative = false;

/** localStorage에서 게임 키만 골라 [key, value] 목록으로 반환 (마이그레이션용) */
export function collectLegacyEntries(ls: Pick<Storage, 'length' | 'key' | 'getItem'>): Array<[string, string]> {
    const entries: Array<[string, string]> = [];
    for (let i = 0; i < ls.length; i++) {
        const key = ls.key(i);
        if (!key || !key.startsWith(KEY_PREFIX)) continue;
        const value = ls.getItem(key);
        if (value === null) continue;
        entries.push([key, value]);
    }
    return entries;
}

export async function initStorage(): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;
    try {
        const { keys } = await Preferences.keys();
        const gameKeys = keys.filter((k) => k.startsWith(KEY_PREFIX));
        if (gameKeys.length > 0) {
            for (const key of gameKeys) {
                const { value } = await Preferences.get({ key });
                if (value !== null) memCache.set(key, value);
            }
        } else {
            // 기존 유저: localStorage 세이브를 Preferences로 1회 이관
            for (const [key, value] of collectLegacyEntries(localStorage)) {
                memCache.set(key, value);
                await Preferences.set({ key, value });
            }
        }
        useNative = true;
    } catch (e) {
        // Preferences 사용 불가 시 localStorage로 동작 (기존과 동일)
        console.warn('[persist] init failed, falling back to localStorage:', e);
        useNative = false;
    }
}

/** raw 문자열 저장 (i18n locale 등 JSON이 아닌 값용) */
export function getRaw(key: string): string | null {
    try {
        return useNative ? memCache.get(key) ?? null : localStorage.getItem(key);
    } catch {
        return null;
    }
}

export function setRaw(key: string, value: string): void {
    try {
        if (useNative) {
            memCache.set(key, value);
            Preferences.set({ key, value }).catch((e) => console.warn('[persist] set failed:', e));
            try { localStorage.setItem(key, value); } catch { /* 미러 실패는 무시 */ }
        } else {
            localStorage.setItem(key, value);
        }
    } catch (e) {
        console.warn('Storage set failed:', e);
    }
}

export const storage = {
    get: <T>(key: string, defaultVal: T): T => {
        try {
            const raw = getRaw(key);
            return raw ? (JSON.parse(raw) as T) : defaultVal;
        } catch {
            return defaultVal;
        }
    },
    set: <T>(key: string, val: T): void => {
        setRaw(key, JSON.stringify(val));
    },
};
